<template>
    <div class="post-page" :key="`post-${id}`">
        <hero-module v-bind="heroModule"
                     :is-post="true"
                     :categories="categoryList"
                     :date="date" />
        <div class="post-page-content"  >
            <section class="post-content" v-if="postBody" v-scroll-reveal="{ delay: 700 }">
                <div class="share-container">
                    <h3 class="share-title">Share with Friends</h3>
                    <ul class="share-links-list">
                        <li v-for="(social, i) in socialLinks"
                            :key="`social-link-${i}`"
                            class="social-media-item">
                            <a :href="social.url"  class="social-media-link"
                               :target="social.network !== 'Email' ? '_blank':''">
                                <font-awesome-icon class="icon" :icon="getSocialIcon(social.network)"/>
                            </a>
                        </li>
                    </ul>
                </div>
                <div class="content-container">
                    <h2 v-if="title" class="h1 post-title">
                        {{ title.rendered }}
                    </h2>
                    <h3 class="post-info">
                        {{ date }} {{ categoryList }}
                    </h3>
                    <div class="post-body" v-html="postBody"></div>
                </div>
            </section>
            <section class="post-links-row">
                <hr class="divider">
                <div class="post-links">
                    <div class="prev-post" v-if="prevPost">
                        <router-link class="post-link prev-post-link" :to="prevPost.slug">
                            <h3 class="post-block-title">Previous Post</h3>
                            <post-block v-bind="prevPost" :id="prevPost.ID" />
                        </router-link>
                    </div>
                    <div class="next-post" v-if="nextPost">
                        <router-link class="post-link next-post-link" :to="nextPost.slug">
                            <h3 class="post-block-title">Next Post</h3>
                            <post-block v-bind="nextPost" :id="nextPost.ID" />
                        </router-link>
                    </div>
                </div>
                <div class="post-button">

                </div>
            </section>
        </div>
        <cta-block-module v-if="cta" v-bind="cta"/>
    </div>
</template>

<script>
    import {EventBus} from '../utils/EventBus'
    import dayjs from 'dayjs'
    import HeroModule from '../modules/HeroModule'
    import {wpPostApi} from '@/utils/api'
    import {razzNextPrevPostApi} from '@/utils/api'
    import PostBlock from '../modules/components/PostBlock'
    import ctaBlockModule from '../modules/ctaBlockModule'
    import ButtonLink from '../modules/components/ButtonLink'
    import faFacebookF from '@fortawesome/fontawesome-free-brands/faFacebookF'
    import faTwitter  from '@fortawesome/fontawesome-free-brands/faTwitter'
    import { faEnvelope }  from '@fortawesome/free-solid-svg-icons/faEnvelope'

    export default {
        name: "Post",
        props: {
            id: [String, Number],
            type: String
        },
        components: {
            HeroModule,
            PostBlock,
            ctaBlockModule,
            ButtonLink
        },
        data() {
            return {
                defaultCTA: wp.settings.defaultCTA,
                socialIcons: {
                    faFacebookF,
                    faTwitter,
                    faEnvelope
                }
            }
        },
        asyncComputed: {
            postContent() {
                return wpPostApi.get(`${this.id}`).then((res) => {
                    this.$emit('pageLoaded');
                    EventBus.setPageLoaded(true);
                    this.$scrollTo('body', 100, {offset: 1});
                    return res.data
                })
            },
            nextPrevPosts() {
                return razzNextPrevPostApi.get(`${this.id}`).then(res => res.data)
            }
        },
        computed: {
            prevPost() {
                return this.nextPrevPosts && this.nextPrevPosts.prev
            },
            nextPost() {
                return this.nextPrevPosts && this.nextPrevPosts.next
            },
            title() {
                return this.postContent && this.postContent.title
            },
            link() {
                return this.postContent && this.postContent.link
            },
            heroModule() {
                return this.modules && this.modules.hero
            },
            date() {
                if (!this.postContent) return '';

                return dayjs(this.postContent.date).format('MM.DD.YYYY');
            },
            categoryList() {
                if (!this.postContent || !this.postContent.categories) return ''

                const categories = this.postContent.categories.map((category) => {
                    return category.name
                });

                if (categories.length === 1) {
                    return categories[0];
                }

                return `| ${categories.join(', ')}`
            },
            socialLinks() {
                return [
                    {network: 'Facebook', url: `https://www.facebook.com/sharer/sharer.php?u=${this.link}`},
                    {network: 'Twitter', url: `https://twitter.com/home?status=${this.link}`},
                    {network: 'Email', url: `mailto:?&subject=I'd like to share a link with you&body=${this.link}`}
                ]
            },
            modules() {
                return this.postContent && this.postContent.acf
            },
            postBody() {
                return this.modules && this.modules.post_content
            },
            ctaOverride() {
                return this.modules && this.modules.override_cta
            },
            cta() {
                return this.ctaOverride ? this.modules.cta_block_override: this.defaultCTA
            },
            yoastMetaTags() {
                return this.modules && this.modules.yoast_meta
            },
            siteName() {
                return wp && wp.site_name || 'Tamarac Village';
            },
            metaTitle() {
                let title = this.yoastMetaTags && this.yoastMetaTags.yoast_wpseo_title;

                if (title) {
                    return title.replace('%%sep%%', '|').replace('%%sitename%%', this.siteName);
                } else {
                    return this.siteName;
                }   
            }
        },
        methods: {
            getSocialIcon(network) {
                switch (network) {
                    case 'Facebook':
                        return this.socialIcons.faFacebookF;
                    case 'Twitter':
                        return this.socialIcons.faTwitter;
                    case 'Email':
                    default:
                        return this.socialIcons.faEnvelope;
                }
            }
        }
    }
</script>

<style lang="scss" scoped>

    .post-page-content {
        position: relative;
        display: flex;
        flex-direction: column;
        @include site-container;
        max-width: 1200px;
        margin: 50px auto 0;
        min-height: min-content;

        @media (max-width: 1200px) {
            padding: 0 40px;
        }

        @media (min-width: 1500px) {
            margin-top: 50px
        }

        @media (min-width: $bp-medium) {
            padding-bottom: 100px;
        }
    }

    .post-content {
        display: flex;

        @media (max-width: 670px) {
            flex-direction: column-reverse;
        }
    }

    .post-links-row {
        margin-top: 100px;
        padding-bottom: 100px;

        .divider {
            border: 1.5px solid $theme-blue;

            @media (max-width: 1000px) {
                margin-bottom: 25px;
            }
        }
    }

    .post-block-title {
        margin-bottom: 0;
    }

    .post-link {
        text-decoration: none;

        color: $theme-blue;

        &:hover {
            cursor: pointer;
            .post-block-title {
                color: $theme-orange;
            }
        }
    }

    .post-links {
        display: flex;

        @media (max-width: 1000px) {
            flex-direction: column;
            justify-content: center;
            align-items: center;
        }

        .prev-post, .next-post {
            width: 50%;
            overflow: hidden;

            @media (max-width: 1000px) {
                width: 100%;
            }

            /deep/ .post-block {
                padding: 20px 0;

                .title {
                    margin-top: 15px;
                }

                .date {
                    color: $theme-blue
                }
            }
        }

        .prev-post {
            padding-right: 40px;
            @media (max-width: 1000px) {
                padding-right: 0;
            }
        }

        .next-post {
            margin-left: auto;
        }
    }

    .post-info {
        margin: 0;
        font-size: 17px;
    }

    .share-container {
        width: 30%;

        @media (max-width: 670px) {
            width: 100%;
            text-align: center;
        }
    }

    .content-container {
        flex: 1;
    }

    .share-title {
        text-transform: capitalize;
    }

    .share-links-list {
        list-style: none;
        padding: 0;
        display: flex;
        flex-direction: column;
        justify-content: flex-start;

        @media (max-width: 670px) {
            flex-direction: row;
            justify-content: center;
            margin-left: -10px;
        }

        .social-media-item {
            width: 35px;
            height: 35px;
            border-radius: 50%;
            background-color: $theme-blue;
            display: flex;
            justify-content: center;
            align-items: center;
            margin: 10px 0;
            transition: all 500ms ease-in-out;

            @media (max-width: 670px) {
                flex-direction: row;
                justify-content: center;
                margin: 0 10px;

                &:first-child {
                    margin-left: 0;
                }

                &:last-child {
                    margin-right: 0;
                }
            }

            &:hover {
                cursor: pointer;
                background-color: rgba(#000, 0.25);

                .social-media-link {
                    color: $theme-blue;
                }
            }

            .social-media-link {
                color: $white;
                font-size: 16px;
                line-height: 16px;
                margin: 0;
                padding: 0;
            }
        }
    }

</style>

<style lang="scss">

    .post-body {
        margin-top: 50px;

        p.p1 {
            margin: 30px 0;
        }

        blockquote {
            margin: 50px 0;
            font-size: 18px;
        }

        img {
            width: 100%;
            height: auto;
            margin: 25px 0 50px;
        }
    }
</style>